import {AfterViewInit, Component, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {delay, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {IddTraining} from '../../../shared/interface/idd-training';
import {IddTrainingService} from '../../../shared/services/idd-training.service';
import {ChartType} from '../idd-training-chart/idd-training-chart.component';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {
  AddIddTrainingCertificateDialogComponent
} from '../add-idd-training-certificate-dialog/add-idd-training-certificate-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';

@Component({
  selector: 'app-all-idd-trainings-list',
  templateUrl: './all-idd-trainings-list.component.html',
  styleUrls: ['./all-idd-trainings-list.component.scss']
})
export class AllIddTrainingsListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  initialized = false;
  readonly chartType = ChartType.Horizontal;
  iddTrainings: IddTraining[] = [];
  displayedColumns: string[] = ['name', 'ofwca', 'rau', 'partner', 'ofwcaAssetsRegional',
    'partnerAssetsRegional', 'status', 'chart', 'actions', ];
  totalData = 0;
  daysToEndOfYear = 0;
  currentYear = '';
  userPermissions: ReplaySubject<UserPermissions>;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private iddTrainingService: IddTrainingService,
    private auth: AuthService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.iddTrainingService.iddTrainingListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<IddTraining>) => {
          this.iddTrainings = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    const currentDate = new Date();
    // if (currentDate.getMonth() < 2 ) {
    //   this.currentYear = (currentDate.getFullYear() - 1).toString();
    // }else {
      this.currentYear = (currentDate.getFullYear() ).toString();
    // }
    const today = new Date();
    const endOfYear = new Date(today.getFullYear(), 11, 31);
    const secondsInDay = 1000 * 60 * 60 * 24;

    this.daysToEndOfYear = Math.ceil((endOfYear.getTime() - today.getTime()) / (secondsInDay));

    this.parseFilters({year: this.currentYear});
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      year: 'string',
      ofwca: 'string',
      partner: 'string',
      assetsRegional: 'string',
      ofwcaAssetsRegional: 'string',
      in$status: 'int[]',
      rauNumber: 'string',
      subjecTraining: 'int[]',
      parentPartner: 'string',
      competitionRequirementsMet: 'int',
    }, filters);
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          ...this.filtersForm.value
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        if (this.initialized) {
          this.parseFilters(state.params);
        } else {
          this.parseFilters({...state.params, year: this.currentYear});
          this.initialized = true;
        }
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.loadListData();
      })
    ;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadListData(): void {
    this.iddTrainingService.loadList(this.paginator.pageIndex, this.paginator.pageSize, this.initialFilters);
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      ...this.filtersForm.value,
    })]);
  }

  downloadReport(): void {
    this.iddTrainingService.downloadReport(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadReportRegional(): void {
    this.iddTrainingService.downloadReportRegional(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  addCertificate(iddTraining: IddTraining): void {
    const dialogRef = this.dialog.open(AddIddTrainingCertificateDialogComponent, {
      width: '70vw',
      height: '550px',
      autoFocus: false,
      data: {
        iddTrainingObject: iddTraining,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      this.loadListData();
    });
  }

  generateCertificates(): void {
    this.iddTrainingService.generateCertificates(this.filtersForm.value)
      .subscribe((response: Uuid) => {
        const dialogRef = this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });

        dialogRef.afterClosed().subscribe(result => {
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu dokumentów wystąpił błąd', error.status.toString(), {duration: 2000});
    });
  }
}
